// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {fetchEffectData} from 'Utils/react-utils';
import {transformAndSortTodos} from 'Utils/workboard-utils';
import SwitchInput from 'Ui/components/common/form/switch-input';
import {Checkbox, Dialog} from '@deere/isg.component-library';
import {DATE_FORMATS, formatTime} from 'Utils/time-utils';
import {deleteTodo, updateTodo} from 'Services/todo-service';
import {connect} from 'react-redux';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {dateCompare} from 'Ui/models/maintenance';
import OnlinkButton from 'Ui/components/common/onlink-button';
import EditLink from 'Ui/components/common/data-table/edit-link';
import {MANAGE_WORKBOARDS} from 'Common/constants/business-activities';
import {isAuthorized} from 'Common/utils/authorization-handler';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

function getDataGridColumns(translations, saveTodo, fetchTodos, openAddTodoDialog, canEditTodo, assignees) {
    return [
        {
            headerName: `${translations.COMPLETE}?`,
            field: 'completed',
            width: 130,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <Checkbox
                        checked={params.row.completed}
                        classes={{
                            root: 'checkbox-root'
                        }}
                        disabled={!canEditTodo}
                        onChange={() => saveTodo(params.row)}
                    />
                );
            }

        },
        {
            headerName: translations.DATE,
            minWidth: 100,
            flex: 1,
            sortComparator: dateCompare,
            field: 'formattedDate'
        },
        {
            headerName: translations.ONLINK_ASSIGNED_TO,
            minWidth: 160,
            flex: 1,
            field: 'userData'
        },
        {
            headerName: translations.ONLINK_TODO,
            field: 'title',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                const {
                    row,
                    value
                } = params;

                return (
                    <EditLink
                        onClick={() => {
                            openAddTodoDialog({
                                todo: row,
                                fetchTodos,
                                assignees
                            });
                        }}
                        requiredMyJdPermissions={[MANAGE_WORKBOARDS]}
                    >
                        {value}
                    </EditLink>
                );
            }
        }
    ];
}

function getFilterComponent(translations, hideCompleted, setHideCompleted, openAddTodoDialog, fetchTodos, canEditTodo, assignees) {
    return (
        <div className='extra-table-options'>
            <SwitchInput
                checked={hideCompleted}
                name='showCompleted'
                onChange={() => setHideCompleted(!hideCompleted)}
                onLabel={translations.ONLINK_HIDE_COMPLETED}
                translations={translations}
            />
            {
                canEditTodo && <OnlinkButton
                    className={'onlink-btn todo-btn'}
                    onClick={() => openAddTodoDialog({
                        fetchTodos,
                        assignees
                    })}
                >
                    {translations.ONLINK_ADD_TODO}
                </OnlinkButton>
            }
        </div>
    );
}

function ManageTodoDialog(props) {
    const {
        todos,
        translations,
        closeManageTodoDialog,
        fetchTodos,
        openConfirmation,
        openAddTodoDialog,
        closeConfirmation,
        assignees,
        teamMembersMap
    } = props;

    const {addToast} = useToast();
    const [loading, setLoading] = React.useState(() => false);
    const [hideCompleted, setHideCompleted] = React.useState(() => true);
    const [filteredTodos, setFilteredTodos] = React.useState([]);

    function filterData() {
        setFilteredTodos(transformAndSortTodos(todos.current, hideCompleted, teamMembersMap));
    }

    async function fetchAndFilterData() {
        await fetchTodos();
        filterData();
    }

    async function saveTodo(todoItem) {
        const todoData = {
            ...todoItem,
            completed: !todoItem.completed,
            dateSelect: formatTime(new Date(), DATE_FORMATS.day)
        };

        setLoading(true);
        await updateTodo(todoData, todoItem.id);
        await fetchAndFilterData();
        setLoading(false);
    }

    async function deleteTodoById(todo) {
        openConfirmation({
            message: replaceTranslationNames(translations.ONLINK_ITEM_THIS_CANNOT_BE_UNDONE, {
                '0': translations.ONLINK_TODO.toLowerCase()
            }),
            title: replaceTranslationNames(translations.ONLINK_DELETE_ITEM, {
                '0': translations.ONLINK_TODO
            }),
            async onContinue() {
                closeConfirmation();

                try {
                    setLoading(true);
                    await deleteTodo(todo.todoId);
                    await fetchAndFilterData();
                    setLoading(false);
                } catch {
                    addToast({
                        message: replaceTranslationNames(translations.ONLINK_CONFIRM_ITEM_DELETION_FAILED, {
                            '0': translations.ONLINK_TODO.toLowerCase()
                        }),
                        type: TOAST_TYPE.ERROR
                    });
                }
            },
            onCancel: closeConfirmation
        });
    }
    React.useEffect(() => fetchEffectData(filterData), [todos.current, hideCompleted]);

    const canEditTodo = isAuthorized({
        myJdPermissions: MANAGE_WORKBOARDS
    }, props);

    return (
        <Dialog
            className='manage-todo-dialog'
            closeHandler={() => {
                closeManageTodoDialog();
            }}
            open={true}
            title={translations.ONLINK_QUICK_TODOS}
            width='xl'
        >
            <div className='table-cover'>
                <DataGridWrapper
                    columns={getDataGridColumns(translations, saveTodo, fetchAndFilterData, openAddTodoDialog, canEditTodo, assignees)}
                    deleteHandler={deleteTodoById}
                    filterComponent={getFilterComponent(translations, hideCompleted, setHideCompleted, openAddTodoDialog, fetchAndFilterData, canEditTodo, assignees)}
                    getRowId={(row) => row.todoId}
                    initialState={{
                        sorting: {
                            sortModel: [{
                                field: 'formattedDate',
                                sort: 'desc'
                            }]
                        }
                    }}
                    loading={loading}
                    rows={filteredTodos}
                    searchFields={['title', 'userData', 'formattedDate']}
                    searchable={true}
                    showDeleteColumn={canEditTodo}
                    slotProps={{
                        root: {
                            disableColumnMenu: true
                        }
                    }}
                    translations={translations}
                />

            </div>
        </Dialog>
    );
}

ManageTodoDialog.propTypes = {
    assignees: PropTypes.array,
    closeConfirmation: PropTypes.func,
    closeManageTodoDialog: PropTypes.func,
    fetchTodos: PropTypes.func,
    myJdPermissions: PropTypes.myJdPermissions,
    onClose: PropTypes.func,
    openAddTodoDialog: PropTypes.func,
    openConfirmation: PropTypes.func,
    teamMembersMap: PropTypes.map,
    todos: PropTypes.object,
    translations: PropTypes.translations
};

export function mapDispatchToProps(dispatch) {
    return {
        closeManageTodoDialog() {
            dispatch(closeDialog(dialogTypes.MANAGE_TODO_DIALOG));
        },
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        },
        openAddTodoDialog(props) {
            dispatch(openDialog(dialogTypes.ADD_TODO_DIALOG, props));
        }
    };
}

export function mapStateToProps(state) {
    return {
        myJdPermissions: state.account.myJdPermissions
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ManageTodoDialog);
